// Resumo de "Filtros ativos" da barra de filtros das telas de fase (Monitorar e Identificar).
//
// O front atual lista TODOS os filtros da barra no resumo, inclusive os que estão no padrão
// ("Selecionar fazenda: Todas as fazendas; Selecionar região: Todas as regiões;
// Selecionar técnico: Todos os técnicos"). O FilterTable da DS descarta do resumo qualquer chave
// cujo valor seja vazio — array vazio incluído —, então a seleção de cada select múltiplo ganha
// uma chave-espelho no Formik com o texto já pronto, no mesmo formato de intervalo que o período
// usa: `labels[0]` vira o rótulo do chip e o segundo elemento do valor, o texto. A chave real
// (`farm_id` e afins) continua no Formik, agora sem campo de mesmo nome — é ela que o submit lê
// para montar a consulta.

export type MultiSelectValue = { value?: string; label?: string };

/**
 * Reproduz `MultiSelectComponent.collectSubtitle`: vazio → "todos"; tudo selecionado → "todos"
 * (mesma comparação `selecionados.length >= total` que o `AppSelectV3` já usa pro
 * `totalSelectLabel` do próprio combobox — CORP-8353 retrabalho: sem isso, o resumo mostra
 * "N selecionados" pra qualquer campo com mais de duas opções, mesmo com tudo marcado);
 * até dois → os nomes; mais que dois → a contagem.
 *
 * `totalOptions` é opcional (retrocompatível com call sites que ainda não passam a contagem
 * total de opções do campo) — sem ele, cai no comportamento anterior.
 */
export function multiSelectSummary(
  values: MultiSelectValue[] | undefined,
  allLabel: string,
  countLabel: (count: number) => string,
  totalOptions?: number,
): [string, string] {
  const names = (values ?? []).map((item) => item?.label ?? item?.value ?? '').filter(Boolean);
  if (names.length === 0) return ['', allLabel];
  if (totalOptions !== undefined && totalOptions > 0 && names.length >= totalOptions) {
    return ['', allLabel];
  }
  if (names.length > 2) return ['', countLabel(names.length)];
  return ['', names.join(', ')];
}

export function summaryFieldName(field: string) {
  return `${field}_summary`;
}

type FormikLike = {
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
};

// CORP-8353: replica o par getMultipleValuesSelected/clearValue que cada `AppSelectV3
// multiple-search` precisa pra manter a chave-espelho `<campo>_summary` sincronizada — sem
// isso, 15 das 16 páginas do PR original ficavam sem a linha de resumo em "Filtros ativos"
// (só `AnimalGroupMonitorTab` já tinha o mecanismo, herdado do CORP-8247).
export function multiSelectFieldHandlers(
  formik: FormikLike,
  field: string,
  allLabel: string,
  countLabel: (count: number) => string,
  totalOptions?: number,
) {
  const summaryField = summaryFieldName(field);
  const setBoth = (values: MultiSelectValue[]) => {
    formik.setFieldValue(field, values ?? []);
    formik.setFieldValue(
      summaryField,
      multiSelectSummary(values ?? [], allLabel, countLabel, totalOptions),
      false,
    );
  };
  return {
    getMultipleValuesSelected: (values: MultiSelectValue[]) => setBoth(values ?? []),
    clearValue: () => setBoth([]),
  };
}

// Par `{ campo, campo_summary }` pra spread direto em `initialValues`/`defaultInitialValues` —
// mesmo par que `multiSelectFieldHandlers` mantém sincronizado depois de montado. Genérico em
// `TField`/`TValue` pra devolver um tipo preciso (`{ [field]: TValue[]; [field_summary]: [string,
// string] }`) e cada call site poder fazer spread direto num objeto tipado, sem `as` no chamador —
// o único cast fica contido aqui, documentado (o par de chaves computadas é logicamente exato,
// só o TS não infere `Record` computado a partir de `[field]`/`[summaryFieldName(field)]`).
export function multiSelectInitialEntry<TField extends string, TValue extends MultiSelectValue>(
  field: TField,
  selected: TValue[] | undefined,
  allLabel: string,
  countLabel: (count: number) => string,
  totalOptions?: number,
): Record<TField, TValue[]> & Record<`${TField}_summary`, [string, string]> {
  return {
    [field]: selected ?? [],
    [summaryFieldName(field)]: multiSelectSummary(
      selected ?? [],
      allLabel,
      countLabel,
      totalOptions,
    ),
  } as Record<TField, TValue[]> & Record<`${TField}_summary`, [string, string]>;
}
